import type { LoaderFunctionArgs, MetaFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import { Link, useLoaderData, useSearchParams } from '@remix-run/react';
import { Search as SearchIcon, FileText, Database } from 'lucide-react';
import { buildMeta } from '~/lib/seo';
import { getAllPosts } from '~/lib/blog.static';
import { Badge } from '~/components/ui/badge';

interface SearchResult {
  type: 'blog' | 'database';
  title: string;
  description: string;
  url: string;
  category?: string;
  date?: string;
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const query = url.searchParams.get('q') || '';

  let results: SearchResult[] = [];

  if (query.trim().length > 0) {
    // Search blog posts
    const allPosts = await getAllPosts();
    const blogResults = allPosts
      .filter(post => {
        const titleMatch = post.title.toLowerCase().includes(query.toLowerCase());
        const excerptMatch = post.excerpt?.toLowerCase().includes(query.toLowerCase());
        const categoryMatch = post.category?.toLowerCase().includes(query.toLowerCase());
        return titleMatch || excerptMatch || categoryMatch;
      })
      .map(post => ({
        type: 'blog' as const,
        title: post.title,
        description: post.excerpt || '',
        url: `/blog/${post.slug}`,
        category: post.category,
        date: post.date,
      }));

    results = blogResults;
  }

  return json({ query, results, totalResults: results.length });
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const title = data?.query
    ? `Search results for "${data.query}"`
    : 'Search';

  return buildMeta({
    title,
    description: 'Search the Lumora website for documentation, blog posts, and game information.',
  });
};

export default function SearchPage() {
  const { query, results, totalResults } = useLoaderData<typeof loader>();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const searchQuery = formData.get('q') as string;
    setSearchParams({ q: searchQuery });
  };

  return (
    <section className="mx-auto max-w-4xl space-y-8 px-4 py-16 sm:px-6 lg:px-8">
      <header className="space-y-4">
        <div className="flex items-center gap-2">
          <SearchIcon className="h-6 w-6 text-lumora-purple" />
          <h1 className="text-4xl font-bold text-foreground">Search</h1>
        </div>
        <p className="text-lg text-muted-foreground">
          Search documentation, blog posts, and game information.
        </p>
      </header>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="relative">
        <div className="relative">
          <SearchIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            name="q"
            defaultValue={query}
            placeholder="Search blog and database..."
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-3 pl-12 pr-4 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:border-lumora-purple focus:outline-none focus:ring-2 focus:ring-lumora-purple/20"
            autoFocus
          />
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Tip: Use specific keywords for better results. Try "combat", "classes", or "crafting".
        </p>
      </form>

      {/* Search Results */}
      {query && (
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-4">
            <h2 className="text-xl font-semibold">
              {totalResults > 0 ? (
                <>
                  Found <span className="text-lumora-purple">{totalResults}</span> {totalResults === 1 ? 'result' : 'results'}
                </>
              ) : (
                <>No results found</>
              )}
            </h2>
          </div>

          {results.length > 0 ? (
            <div className="space-y-4">
              {results.map((result, index) => (
                <SearchResultCard key={`${result.type}-${index}`} result={result} />
              ))}
            </div>
          ) : query && (
            <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-12 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                <SearchIcon className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                No results found for "{query}"
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Try different keywords or browse our content directly.
              </p>
              <div className="flex gap-4 justify-center">
                <Link
                  to="/blog"
                  className="text-sm text-lumora-purple hover:underline"
                >
                  View Blog Posts
                </Link>
                <Link
                  to="/database"
                  className="text-sm text-lumora-purple hover:underline"
                >
                  Browse Database
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search Tips */}
      {!query && (
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-8">
          <h3 className="text-lg font-semibold mb-4">Search Tips</h3>
          <ul className="space-y-2 text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2">
              <span className="text-lumora-purple">•</span>
              <span>Use specific keywords like "combat skills" or "crafting system"</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-lumora-purple">•</span>
              <span>Search works across blog posts and game database</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-lumora-purple">•</span>
              <span>Results are sorted by relevance</span>
            </li>
          </ul>

          <div className="mt-6 grid grid-cols-2 gap-4">
            <Link
              to="/blog"
              className="flex items-center gap-2 text-sm text-lumora-purple hover:underline"
            >
              <FileText className="h-4 w-4" />
              Blog
            </Link>
            <Link
              to="/database"
              className="flex items-center gap-2 text-sm text-lumora-purple hover:underline"
            >
              <Database className="h-4 w-4" />
              Database
            </Link>
          </div>
        </div>
      )}
    </section>
  );
}

function SearchResultCard({ result }: { result: SearchResult }) {
  const icon = result.type === 'blog' ? FileText : Database;
  const Icon = icon;

  return (
    <Link
      to={result.url}
      className="block rounded-lg border border-gray-200 dark:border-gray-700 p-6 transition-all hover:border-lumora-purple hover:shadow-md"
    >
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4 text-lumora-purple" />
        <Badge variant="secondary" className="text-xs">
          {result.type === 'doc' ? 'Documentation' : result.type === 'blog' ? 'Blog' : 'Database'}
        </Badge>
        {result.category && (
          <Badge variant="outline" className="text-xs">
            {result.category}
          </Badge>
        )}
      </div>
      <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-lumora-purple">
        {result.title}
      </h3>
      <p className="text-gray-600 dark:text-gray-400 line-clamp-2">{result.description}</p>
      {result.date && (
        <p className="mt-2 text-sm text-gray-500">
          {new Date(result.date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </p>
      )}
    </Link>
  );
}
